import React, { useEffect, useRef, useState } from 'react';

import { Select } from 'antd';
import { isArray } from 'lodash';
import { sleep } from 'utils/delay';

import { useGetProductEnums } from 'hooks';
import { findProductPropertyEnums } from 'pages/product-full-view/components/product-detail-view/utils';

import './CountryEditor.less';

const { Option } = Select;

export const CountryEditor = (props) => {
  const {
    initialValue,
    onValueChange,
    mode = 'multiple',
    listString = true,
    column,
  } = props;

  const { productEnums } = useGetProductEnums();

  const countryCodeEnum = findProductPropertyEnums(
    'CountryCodeEnum',
    productEnums
  );

  const processValue = (value) => {
    if (isArray(value)) {
      return value;
    } else if (value) {
      return value?.split(', ') || [];
    } else {
      return [];
    }
  };

  const [country, setCountry] = useState(processValue(initialValue));
  const [editing, setEditing] = useState(true);
  const refContainer = useRef(null);
  const refSelect = useRef();
  const refValue = useRef(undefined);

  const focus = () => {
    setTimeout(() => {
      refSelect.current?.focus && refSelect.current.focus();
    }, [100]);
  };

  useEffect(() => {
    focus();
  }, []);

  const getCountryListInString = (countryValue) => {
    return listString ? countryValue : countryValue.join(', ');
  };

  // A highlighted (arrow-key) option wins over the selected countries
  const emitValue = (nextCountry) => {
    const pendingValue = refValue.current;
    onValueChange(
      getCountryListInString(
        pendingValue === undefined ? nextCountry : pendingValue
      )
    );
  };

  const updateCountry = (nextCountry) => {
    setCountry(nextCountry);
    emitValue(nextCountry);
  };

  useEffect(() => {
    if (!editing) {
      props.stopEditing();
    }
  }, [editing]);

  const handleChange = (value) => {
    if (!value) {
      updateCountry([]);
      return;
    }
    if (typeof value === 'string') {
      updateCountry([value]);
      return;
    }
    updateCountry(value);
  };

  const handleKeyInMultipleMode = async (e) => {
    if (e.key === 'Control') {
      await sleep(100);

      const $dropDownActiveItem = document.querySelector(
        'div.ant-select-item-option-active > div.ant-select-item-option-content > span.option-item-value'
      );

      if ($dropDownActiveItem) {
        const nextValue = [...country];

        const activeValue = $dropDownActiveItem.getAttribute('value');

        const foundActiveValueIdx = country.findIndex(
          (valueItem) => valueItem === activeValue
        );

        if (foundActiveValueIdx !== -1) {
          nextValue.splice(foundActiveValueIdx, 1);
        } else {
          nextValue.push(activeValue);
        }

        updateCountry(nextValue);
      }
    }
  };

  const handleKeyInSingleMode = async (e) => {
    if (e.key === 'Delete') {
      refValue.current = [];
      updateCountry([]);
      setEditing(false);
      return;
    }

    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
    } else {
      await sleep(100);
      const $dropDownActiveItem = document.querySelector(
        'div.ant-select-item-option-active > div.ant-select-item-option-content > span.option-item-value'
      );

      if ($dropDownActiveItem) {
        const activeValue = $dropDownActiveItem.getAttribute('value');
        const nextValue = [activeValue];

        refValue.current = nextValue;
        emitValue(country);
      }
    }
  };

  const handleInputKeyDown = async (e) => {
    if (e.key === 'Delete') {
      updateCountry([]);
      setEditing(false);
      return;
    }

    if (mode === 'multiple') {
      await handleKeyInMultipleMode(e);
    }

    if (mode === 'single') {
      await handleKeyInSingleMode(e);
    }
  };

  const width = column.getActualWidth() || 300;

  return (
    <div
      id='select'
      ref={refContainer}
      className='ag-editor__country'
      tabIndex={1}
      style={{ width }}
    >
      <Select
        ref={refSelect}
        allowClear
        mode={mode}
        style={{ width: '100%' }}
        placeholder='Please select'
        onChange={handleChange}
        value={country}
        onInputKeyDown={handleInputKeyDown}
        virtual={true}
      >
        {countryCodeEnum?.map((countryItem) => {
          return (
            <Option
              key={countryItem?.enumDisplayName}
              value={countryItem?.enumDisplayName}
              style={{ display: 'flex', alignItems: 'center' }}
              className='ag-editor__country-option'
            >
              <span
                className='option-item-value'
                value={countryItem.enumDisplayName}
              >
                {countryItem.enumDisplayName}
              </span>
            </Option>
          );
        })}
      </Select>
    </div>
  );
};
